import React from 'react';
import { NEED_PRIORITY_LABELS } from '~clients/config/carePlanEnums';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Modal } from '~components';
import { FieldProps, Fields } from '~components/form-fields';

import { CarePlanNeed, NeedPriority } from '~clients/models';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import { useCreateCarePlanNeedMutation, useUpdateCarePlanNeedMutation } from '~clients/actions/carePlans';

interface ComponentProps {
  planId: string;
  /** Present → edit mode; absent → create mode. */
  need?: CarePlanNeed;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

interface NeedValues {
  [key: string]: string | number | boolean | object | null;
  title: string;
  description: string;
  priority: NeedPriority | '';
}

const PRIORITY_OPTIONS = (Object.keys(NEED_PRIORITY_LABELS) as NeedPriority[]).map((value) => ({
  id: value,
  name: NEED_PRIORITY_LABELS[value],
}));

const needFields: FieldProps = {
  title: { type: 'text', caption: 'Title', dataCy: 'care-plan-need-title' },
  description: { type: 'textarea', caption: 'Description', required: true, dataCy: 'care-plan-need-description' },
  priority: {
    type: 'select',
    caption: 'Priority',
    options: PRIORITY_OPTIONS,
    placeholder: '-- Select a priority --',
    dataCy: 'care-plan-need-priority',
  },
};
const definitions = Object.entries(needFields).map(([key, props]) => ({ key, ...props }));

const validationSchema = Yup.object().shape({
  description: Yup.string().required('Description is required'),
});

const NeedFormModal: React.FC<ComponentProps> = ({ planId, need, isOpen, setIsOpen }) => {
  const { showToast } = useToast();
  const [createNeed, { isLoading: isCreating, error: createError, reset: resetCreate }] =
    useCreateCarePlanNeedMutation();
  const [updateNeed, { isLoading: isUpdating, error: updateError, reset: resetUpdate }] =
    useUpdateCarePlanNeedMutation();

  const { invalidFields, validate } = useFieldValidationHighlights(validationSchema);

  const isEdit = need !== undefined;
  const isSaving = isCreating || isUpdating;
  // Show the error from the mutation that actually ran for this mode, not whichever
  // happens to be set — avoids surfacing a stale error from the other mutation.
  const error = isEdit ? updateError : createError;

  // Instance is reused across close/reopen in the same mode, so a failed attempt's error
  // would otherwise resurface on the next open. Clear both on close.
  React.useEffect(() => {
    if (!isOpen) {
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, resetCreate, resetUpdate]);

  const initialValues: NeedValues = {
    title: need?.title ?? '',
    description: need?.description ?? '',
    priority: need?.priority ?? '',
  };

  const onSubmit = async (values: NeedValues) => {
    const body = {
      title: values.title || null,
      description: values.description,
      priority: (values.priority || null) as NeedPriority | null,
    };
    try {
      if (isEdit) {
        await updateNeed({ id: need.id, ...body }).unwrap();
      } else {
        await createNeed({ planId, ...body }).unwrap();
      }
      showToast('success', `Assessed need ${isEdit ? 'updated' : 'added'}`);
      setIsOpen(false);
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header={isEdit ? 'Edit Assessed Need' : 'Add Assessed Need'}
      includeFooter={false}
      bodyClassName="care-plan-form-body"
      size="xl"
    >
      {error !== undefined && <ErrorResponse dataCy="care-plan-need-form-error" message={getErrorMessage(error)} />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        validate={validate}
        validateOnMount={false}
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, setFieldTouched, touched, dirty }) => (
          <Form>
            <Fields
              definitions={definitions}
              values={values}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              touched={touched}
              invalidFields={invalidFields}
            />
            <div className="care-plan-need-form-actions">
              <Button size="sm" color="warning" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                color="success"
                disabled={!dirty}
                loading={isSaving}
                dataCy="care-plan-need-save"
              >
                {isEdit ? 'Save' : 'Add Need'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default NeedFormModal;
